import type { Payload } from 'payload'
import { createLogger } from '@/lib/logger'

const logger = createLogger('YouTube')

const YOUTUBE_API_BASE = 'https://www.googleapis.com/youtube/v3'
export const YOUTUBE_CHANNEL_HANDLE = 'efficientapp'
const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/
const YOUTUBE_ID_WITH_SUFFIX_PATTERN = /^([a-zA-Z0-9_-]{11})(?:[?&#].*)$/
const YOUTUBE_HOST_WITHOUT_PROTOCOL_PATTERN =
  /^(?:(?:www|m)\.)?(?:youtube(?:-nocookie)?\.com|youtu\.be)\//i

type YouTubeVideo = {
  id: number
  name: string
  youtubeId: string
  thumbnailUrl: string
}

// === Thumbnail URL helpers ===

// YouTube thumbnail quality tiers — all native 16:9 (no letterboxing):
//   high   = maxresdefault.jpg (1280×720, ~80–200 KB) — minimal compression
//   medium = hq720.jpg          (1280×720, ~30–60 KB) — same dimensions, more compressed
//   small  = mqdefault.jpg      (320×180,  ~10–20 KB) — for buttons / list thumbnails
export type YoutubeThumbnailQuality = 'high' | 'medium' | 'small'

const YOUTUBE_QUALITY_FILENAMES: Record<YoutubeThumbnailQuality, string> = {
  high: 'maxresdefault.jpg',
  medium: 'hq720.jpg',
  small: 'mqdefault.jpg',
} as const

// hqdefault.jpg is the only YouTube thumbnail URL generated for *every* video
// regardless of upload resolution — the safety net when hq720/maxres miss.
// It's 4:3 with letterboxing for 16:9 sources; object-cover crops the bars.
const YOUTUBE_FALLBACK_FILENAME = 'hqdefault.jpg'

const YOUTUBE_THUMBNAIL_FILENAME_PATTERN =
  /(?:maxresdefault|hqdefault|sddefault|mqdefault|hq720|default)\.jpg/

export function ytIdToThumbnailUrl({
  id,
  quality = 'medium',
}: {
  id: string
  quality?: YoutubeThumbnailQuality
}): string {
  return `https://img.youtube.com/vi/${id}/${YOUTUBE_QUALITY_FILENAMES[quality]}`
}

export function ytFallbackThumbnailUrl(url: string): string {
  return url.replace(YOUTUBE_THUMBNAIL_FILENAME_PATTERN, YOUTUBE_FALLBACK_FILENAME)
}

export function setYoutubeThumbnailQuality(url: string, quality: YoutubeThumbnailQuality): string {
  return url.replace(YOUTUBE_THUMBNAIL_FILENAME_PATTERN, YOUTUBE_QUALITY_FILENAMES[quality])
}

export function getYoutubeIdFromInput(rawInput?: string | null): string | null {
  if (!rawInput) return null

  let value = rawInput.trim().replaceAll('&amp;', '&')
  if (!value) return null

  if (YOUTUBE_ID_PATTERN.test(value)) return value
  const suffixedId = value.match(YOUTUBE_ID_WITH_SUFFIX_PATTERN)?.[1]
  if (suffixedId) return suffixedId

  if (YOUTUBE_HOST_WITHOUT_PROTOCOL_PATTERN.test(value)) value = `https://${value}`

  try {
    const parsedUrl = new URL(value)
    const host = parsedUrl.hostname.toLowerCase()

    if (host === 'youtu.be') {
      const pathParts = parsedUrl.pathname.split('/').filter(Boolean)
      const candidate = pathParts[0]
      return candidate && YOUTUBE_ID_PATTERN.test(candidate) ? candidate : null
    }

    if (
      host !== 'youtube.com' &&
      !host.endsWith('.youtube.com') &&
      host !== 'youtube-nocookie.com' &&
      !host.endsWith('.youtube-nocookie.com')
    ) {
      return null
    }

    const watchId = parsedUrl.searchParams.get('v')
    if (watchId && YOUTUBE_ID_PATTERN.test(watchId)) return watchId

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean)
    const shortTypes = new Set(['embed', 'shorts', 'live', 'v'])
    if (pathParts.length >= 2 && shortTypes.has(pathParts[0])) {
      return YOUTUBE_ID_PATTERN.test(pathParts[1]) ? pathParts[1] : null
    }

    return null
  } catch {
    return null
  }
}

// === Formatting ===

export function formatCount(num: number): string {
  if (num >= 1_000_000_000) {
    return `${(num / 1_000_000_000).toFixed(1)}B`
  }
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`
  }
  return `${num}`
}

async function youtubeApiFetch<T>(path: string): Promise<T | null> {
  const apiKey = process.env.YOUTUBE_API_KEY
  if (!apiKey) {
    logger.error('YOUTUBE_API_KEY not set')
    return null
  }

  const url = new URL(`${YOUTUBE_API_BASE}/${path}`)
  url.searchParams.set('key', apiKey)

  let response: Response
  try {
    // API key is restricted to *.efficient.app/* and efficient.app/* in Google Cloud Console
    response = await fetch(url, {
      headers: { Referer: 'https://efficient.app/' },
      next: { revalidate: 21600 }, // 6 hours
    })
  } catch (error) {
    logger.error(`YouTube API fetch failed for path ${path}:`, error)
    return null
  }

  if (!response.ok) {
    logger.error(`YouTube API error (${path}): ${response.status} ${response.statusText}`)
    return null
  }

  try {
    return (await response.json()) as T
  } catch (error) {
    logger.error(`YouTube API JSON parse failed for path ${path}:`, error)
    return null
  }
}

type YouTubeChannelResponse = {
  items: Array<{
    statistics: { subscriberCount: string }
  }>
}

export async function fetchLatestVideos(
  payloadInstance: Payload,
  options?: { limit?: number },
): Promise<YouTubeVideo[]> {
  const limit = options?.limit ?? 4

  const { docs: videoDocs } = await payloadInstance.find({
    collection: 'video',
    where: {
      youtubeId: { exists: true },
      tags: { not_equals: 'short' },
      datePublished: { less_than_equal: new Date().toISOString() },
    },
    select: { name: true, youtubeId: true },
    sort: ['-createdAt'],
    depth: 0,
    limit,
    pagination: false,
  })

  return videoDocs
    .filter(
      (doc): doc is typeof doc & { youtubeId: string } =>
        typeof doc.youtubeId === 'string' && doc.youtubeId.length > 0,
    )
    .map((doc) => ({
      id: doc.id,
      name: doc.name,
      youtubeId: doc.youtubeId,
      thumbnailUrl: ytIdToThumbnailUrl({ id: doc.youtubeId, quality: 'small' }),
    }))
}

type YouTubeVideoResponse = {
  items: Array<{
    statistics: { viewCount: string }
  }>
}

export async function fetchVideoViewCount(videoId: string): Promise<string | null> {
  const data = await youtubeApiFetch<YouTubeVideoResponse>(`videos?part=statistics&id=${videoId}`)

  const count = parseInt(data?.items?.[0]?.statistics?.viewCount ?? '0', 10)
  if (count === 0) return null
  return `${formatCount(count)} views`
}

export async function fetchSubscriberCount(): Promise<string | null> {
  const data = await youtubeApiFetch<YouTubeChannelResponse>(
    `channels?part=statistics&forHandle=${YOUTUBE_CHANNEL_HANDLE}`,
  )

  const count = parseInt(data?.items?.[0]?.statistics?.subscriberCount ?? '0', 10)
  if (count === 0) return null
  return formatCount(count)
}
